import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * The per-page region inside the app `<main>`. `main` provides the bounded
 * height (viewport minus the app header) and does not scroll itself; the shell
 * decides what happens within that box.
 *
 * - `scroll` (default) — content flows and the shell scrolls vertically. Use for
 *   dashboards, forms, detail pages: anything that can be taller than the screen.
 * - `fill` — the shell is a non-scrolling flex column that exactly fills the
 *   available height. Direct children lay out top-to-bottom; give the one that
 *   should absorb the slack `flex-1 min-h-0` (e.g. a table region whose body
 *   scrolls internally while a pagination footer stays pinned). Use for
 *   full-height table pages where the page must fit the viewport regardless of
 *   row count / page size.
 */
function PageShell({
  variant = 'scroll',
  className,
  ...props
}: React.ComponentProps<'div'> & { variant?: 'scroll' | 'fill' }) {
  return (
    <div
      data-slot="page-shell"
      data-variant={variant}
      className={cn(
        'p-6 lg:p-8',
        variant === 'scroll' ? 'flex-1 overflow-y-auto' : 'flex min-h-0 flex-1 flex-col',
        className,
      )}
      {...props}
    />
  );
}

export { PageShell };
